/**
 * Request schemas for products, classifications and locations.
 *
 * Money is integer pence throughout — never a float, never a decimal string.
 * The SPA sends pence and formats with formatGBP at the edge; a form that
 * accepted "12.50" and multiplied by 100 is how rounding bugs get into invoices.
 */
import { z } from 'zod';
/** Reused wherever a monetary field appears. */
const pence = z
    .number()
    .int('Must be a whole number of pence')
    .min(0, 'Cannot be negative')
    // £100,000 a unit is not a real consumable; it is a misplaced decimal point.
    .max(10_000_000, 'That looks too large — amounts are in pence');
const name = z.string().trim().min(1, 'Enter a name').max(160);
/** Stable machine key for classifications and locations, e.g. "injectable". */
const key = z
    .string()
    .trim()
    .toLowerCase()
    .min(1)
    .max(40)
    .regex(/^[a-z0-9][a-z0-9_-]*$/, 'Use letters, numbers, hyphens or underscores');
/**
 * What one pack holds, for products used in parts ("1 vial = 100 units").
 * In hundredths; see packages/core/src/targets/recipes.ts. Both optional: a
 * product that is only ever used whole needs neither.
 */
const packFields = {
    packContentsHundredths: z.number().int().min(1).max(1_000_000).optional(),
    contentUnit: z.string().trim().min(1).max(20).nullable().optional(),
};
export const createProductSchema = z.object({
    name,
    brand: z.string().trim().max(120).optional(),
    category: z.string().trim().max(120).optional(),
    classificationId: z.string().uuid().optional(),
    unitCostPence: pence.default(0),
    /** Only meaningful for retail lines; null for treatment products. */
    retailPricePence: pence.nullable().optional(),
    /** Reorder threshold. 0 means "never warn". */
    minLevel: z.number().int().min(0).max(1_000_000).default(0),
    unitLabel: z.string().trim().min(1).max(40).default('unit'),
    notes: z.string().trim().max(2000).optional(),
    ...packFields,
});
/**
 * Inline editing on the Manage stock page. Every field optional — the UI sends
 * only what changed, so an untouched field cannot be clobbered by a stale value
 * from a form that was open while someone else edited the row.
 */
export const updateProductSchema = z
    .object({
    name: name.optional(),
    brand: z.string().trim().max(120).nullable().optional(),
    category: z.string().trim().max(120).nullable().optional(),
    classificationId: z.string().uuid().nullable().optional(),
    unitCostPence: pence.optional(),
    retailPricePence: pence.nullable().optional(),
    minLevel: z.number().int().min(0).max(1_000_000).optional(),
    unitLabel: z.string().trim().min(1).max(40).optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
    ...packFields,
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to change' });
/**
 * A label as a person types it → the stable key imports and the prototype's
 * `product.type` match on.
 *
 * The key exists for machines: spreadsheet imports resolve a classification by
 * it, and it is immutable once set for exactly that reason. Asking a clinic to
 * invent "treatment_products" for a thing they call "Treatment products" is
 * asking them to do a computer's job, so every client would end up deriving it
 * — and two clients deriving it slightly differently is how the same
 * classification ends up with two keys and an import silently stops matching.
 * Derived in one place instead.
 *
 * Falls back to `item` when a label has no alphanumerics at all (an emoji, say).
 * That can collide, but a collision is a visible 409 rather than an invalid key.
 */
export function slugify(label) {
    const slug = label
        .toLowerCase()
        .normalize('NFKD')
        // Strip combining marks so "Crème" becomes "creme" rather than "crme".
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 40)
        // A trailing underscore can survive the slice.
        .replace(/_+$/, '');
    return slug === '' ? 'item' : slug;
}
export const createClassificationSchema = z
    .object({
    // Optional: derived from the label when a client does not supply one. The
    // import path still passes an explicit key so existing sheets keep matching.
    key: key.optional(),
    label: name,
    isRetail: z.boolean().default(false),
    sortOrder: z.number().int().min(0).max(999).default(0),
})
    .transform((v) => ({ ...v, key: v.key ?? slugify(v.label) }));
export const updateClassificationSchema = z
    .object({
    label: name.optional(),
    isRetail: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(999).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to change' });
export const createLocationSchema = z
    .object({ key: key.optional(), label: name })
    .transform((v) => ({ ...v, key: v.key ?? slugify(v.label) }));
export const updateLocationSchema = z.object({ label: name });
/** Sort options for the inventory table — spec §4 says the columns are sortable. */
export const PRODUCT_SORTS = ['name', 'qty', 'value', 'expiry', 'category'];
export const listProductsSchema = z.object({
    /** Free text over name, brand and category. */
    q: z.string().trim().max(120).optional(),
    classificationId: z.string().uuid().optional(),
    sort: z.enum(PRODUCT_SORTS).default('name'),
    direction: z.enum(['asc', 'desc']).default('asc'),
    /** Archived products stay for history and are hidden unless asked for. */
    includeArchived: z.coerce.boolean().default(false),
});
// -------------------------------------------------------------- add stock
/** ISO calendar date, e.g. 2026-09-30. Not an instant — expiry is a date. */
const localDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-09-30')
    .refine((d) => !Number.isNaN(Date.parse(d)), 'That date does not exist');
/**
 * Receiving stock. design/Clinic Stock v2.dc.html:3237 (`confirmAdd`).
 *
 * `unitCostPence` is optional: omit it and the batch inherits the product's
 * current cost. Supply it and it becomes BOTH the batch's cost at receipt and
 * the product's new cost, matching the prototype — the latest purchase price is
 * what the clinic wants quoted going forward, while history keeps what was
 * actually paid.
 */
export const addStockSchema = z.object({
    qty: z
        .number()
        .int('Whole units only')
        .min(1, 'Add at least one unit')
        .max(1_000_000, 'That is more units than any clinic holds'),
    lot: z.string().trim().max(80).optional(),
    /** Null for stock that genuinely never expires, e.g. equipment. */
    expiry: localDate.nullable().optional(),
    locationId: z.string().uuid().optional(),
    unitCostPence: pence.optional(),
    /** Defaults to today in the account's timezone, set by the API not the client. */
    receivedOn: localDate.optional(),
});
/**
 * Correcting a batch that was booked in with the wrong lot number or expiry.
 *
 * These two are what a batch IS, as far as a recall or an expiry warning is
 * concerned, so a typo has to be fixable. Quantity is deliberately not here:
 * a wrong quantity is corrected through the ledger (a void, a stock count),
 * never by editing the number, because the ledger is what the number is
 * derived from. At least one of the two must be sent.
 */
export const updateBatchSchema = z
    .object({
    lot: z.string().trim().max(80).nullable().optional(),
    expiry: localDate.nullable().optional(),
})
    .refine((v) => v.lot !== undefined || v.expiry !== undefined, {
    message: 'Nothing to change',
});
// ------------------------------------------------------------- logging usage
/**
 * Why stock left the shelf. design/Clinic Stock v2.dc.html:3302
 * (`purposeLabels`), verbatim.
 */
export const USAGE_PURPOSES = [
    'treatment',
    'sold',
    'gift',
    'training',
    'damaged',
    'lost',
    'other',
];
export const USAGE_PURPOSE_LABEL = {
    treatment: 'Treatment',
    sold: 'Sold',
    gift: 'Gifted / sample',
    training: 'Training / demo',
    damaged: 'Damaged',
    lost: 'Lost / missing',
    other: 'Other',
};
/** Purposes that represent stock lost rather than used or sold. */
export const WASTE_PURPOSES = ['damaged', 'lost'];
export const logUsageSchema = z.object({
    qty: z
        .number()
        .int('Whole units only')
        .min(1, 'Log at least one unit')
        .max(100_000, 'That is more units than any clinic holds'),
    purpose: z.enum(USAGE_PURPOSES),
    note: z.string().trim().max(500).optional(),
    /**
     * What was actually charged, for a retail sale. Sent explicitly rather than
     * read from the product, because promotions change it per sale and the ledger
     * must record the price that was really taken.
     */
    pricePence: pence.nullable().optional(),
    occurredOn: localDate.optional(),
    /**
     * Take from this batch instead of the one that expires first.
     *
     * Oldest-first is the default and stays the default: leave this out and the
     * server decides. It exists for the case where the vial in someone's hand is
     * not the one the app would have picked; recording a different lot from the
     * one actually used would make lot tracing wrong, which is worse than using
     * stock out of order. The whole quantity must come from this one batch.
     */
    batchId: z.string().uuid().optional(),
});
// ---------------------------------------------------------------- write-offs
/**
 * Why stock was written off. `expired` is the common case and the one the
 * expiry alert offers in one tap; the rest exist so a write-off never has to be
 * mislabelled to fit.
 */
export const WRITE_OFF_REASONS = [
    'expired',
    'damaged',
    'contaminated',
    'recalled',
    'lost',
    'other',
];
/** Throwing away what is left in an open pack. */
export const discardOpenPackSchema = z.object({
    reason: z.enum(WRITE_OFF_REASONS),
});
export const WRITE_OFF_REASON_LABEL = {
    expired: 'Expired',
    damaged: 'Damaged',
    contaminated: 'Contaminated',
    recalled: 'Recalled',
    lost: 'Lost / missing',
    other: 'Other',
};
export const writeOffSchema = z.object({
    /** Omit to write off everything left in the batch — the one-tap case. */
    qty: z.number().int().min(1).max(1_000_000).optional(),
    reason: z.enum(WRITE_OFF_REASONS),
    note: z.string().trim().max(500).optional(),
});
/** Filter for the expiry page. `window` is a custom horizon in days. */
export const expiryQuerySchema = z.object({
    bucket: z.enum(['expired', 'urgent', 'soon', 'all']).default('all'),
    window: z.coerce.number().int().min(1).max(3650).optional(),
});
// ------------------------------------------------------------- stock counting
/**
 * Why a physical count differs from the system — design/PROTOTYPE-SPEC.md §7,
 * "any variance requires a reason". Verbatim from the prototype's `countReasons`
 * (design/Clinic Stock v2.dc.html:4071).
 *
 * The reason is the entire point of the feature. A stocktake that silently
 * rewrites quantities tells a clinic what it has; a stocktake that records why
 * tells them whether they have a theft problem, a fridge problem, or a
 * data-entry problem.
 */
export const VARIANCE_REASONS = [
    'miscount',
    'gift',
    'lost',
    'breakage',
    'expired',
    'other',
];
export const VARIANCE_REASON_LABEL = {
    miscount: 'Miscount / typo',
    gift: 'Gifted / samples',
    lost: 'Lost / missing',
    breakage: 'Breakage / damaged',
    expired: 'Expired, thrown out',
    other: 'Other',
};
export const stockCountLineSchema = z.object({
    productId: z.string().uuid(),
    /** What was physically on the shelf. Zero is a real answer, not a blank. */
    countedQty: z
        .number()
        .int('Whole units only')
        .min(0, 'A count cannot be negative')
        .max(1_000_000, 'That is more units than any clinic holds'),
    /**
     * The system quantity the counter was shown.
     *
     * Optimistic concurrency: a stocktake is slow and the clinic keeps working
     * through it, so stock can move between reading the sheet and applying it.
     * Without this, a treatment logged mid-count is silently erased by the
     * variance — the count would "correct" a number that was already correct.
     */
    expectedQty: z.number().int().min(0).optional(),
    reason: z.enum(VARIANCE_REASONS).optional(),
    /** Required when the reason is `other`, per the prototype's note field. */
    reasonNote: z.string().trim().max(200).optional(),
    /**
     * Expiry for stock that turns up in a count. Optional: making it required
     * would stall a shelf-walk, and found stock is usually a miscount of what is
     * already there. See docs/DECISIONS.md §2 for what this costs.
     */
    foundExpiry: localDate.nullable().optional(),
});
export const stockCountSchema = z.object({
    lines: z
        .array(stockCountLineSchema)
        .min(1, 'Count at least one product')
        .max(2_000, 'That is more lines than one count should carry'),
    note: z.string().trim().max(500).optional(),
});
/** A line needs a reason exactly when it disagrees with the system. */
export function varianceNeedsReason(countedQty, systemQty) {
    return countedQty !== systemQty;
}
/** The human label for a line's reason, including the free-text `other`. */
export function varianceReasonLabel(reason, note) {
    if (reason === 'other') {
        const trimmed = note?.trim().slice(0, 60);
        return trimmed ? `Other — ${trimmed}` : 'Other';
    }
    return VARIANCE_REASON_LABEL[reason];
}
// --------------------------------------------------------------- promotions
/**
 * Creating a promotion. design/PROTOTYPE-SPEC.md §12.
 *
 * `value` means different things per `kind` and is validated per `kind`: whole
 * percent 1–100, or a discount in pence. Validating it as one range would let
 * "500% off" through as easily as "£5 off", and the two are one dropdown apart
 * in the UI.
 *
 * The database carries the same two bounds as CHECK constraints. That is not
 * redundant — this schema rejects a bad request politely, and the constraint
 * makes a bad row impossible whatever calls the database. Money gets both.
 */
export const createPromotionSchema = z
    .object({
    productId: z.string().uuid(),
    kind: z.enum(['percent', 'amount']),
    value: z.number().int('Must be a whole number'),
    startsOn: localDate,
    /** Null is meaningful: a promotion that runs until someone stops it. */
    endsOn: localDate.nullable().optional(),
    note: z.string().trim().max(200).optional(),
})
    .refine((v) => (v.kind === 'percent' ? v.value >= 1 && v.value <= 100 : v.value >= 1), {
    message: 'A percentage must be 1–100; an amount must be at least 1p',
    path: ['value'],
})
    .refine((v) => !v.endsOn || v.endsOn >= v.startsOn, {
    message: 'The end date cannot be before the start date',
    path: ['endsOn'],
});
/**
 * Editing one. The product cannot move: a promotion is an offer on a specific
 * product, and repointing it would silently rewrite what past sales were
 * attributed to. Make a new one instead.
 */
export const updatePromotionSchema = z
    .object({
    kind: z.enum(['percent', 'amount']).optional(),
    value: z.number().int('Must be a whole number').min(1).optional(),
    startsOn: localDate.optional(),
    endsOn: localDate.nullable().optional(),
    note: z.string().trim().max(200).nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to change' });
//# sourceMappingURL=schemas.js.map